import React, { useState } from "react";
import { IoWarningOutline, IoCheckmark, IoDownloadOutline, IoCopyOutline } from "react-icons/io5";
import { MachineAccountService } from "../../services/machineAccountsApi";

const WarningIcon = IoWarningOutline as any;
const CheckIcon = IoCheckmark as any;
const DownloadIcon = IoDownloadOutline as any;
const CopyIcon = IoCopyOutline as any;

interface ApiKeyModalProps {
  apiKey: string;
  keyLabel: string;
  serviceAccountEmail: string;
  service?: MachineAccountService;
  personalDatasetId?: string;
  realtimeViewId?: string;
  onClose: () => void;
}

const ApiKeyModal: React.FC<ApiKeyModalProps> = ({
  apiKey,
  keyLabel,
  serviceAccountEmail,
  service,
  personalDatasetId,
  realtimeViewId,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const lines = [
      "# Telcofy API Key",
      `# Label: ${keyLabel}`,
      `# Service Account: ${serviceAccountEmail}`,
    ];
    if (service) lines.push(`# Service: ${service}`);
    if (personalDatasetId) lines.push(`# Personal Dataset: ${personalDatasetId}`);
    if (realtimeViewId) lines.push(`# Realtime View: ${realtimeViewId}`);
    lines.push(`# Created: ${new Date().toISOString()}`, "", `API_KEY="${apiKey}"`, "");
    const content = lines.join("\n");
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `telcofy-apikey-${keyLabel}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40 p-4 animate-fade-in">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-surface shadow-card-hover tf-scroll">
        <div className="border-b border-paper-300/70 px-6 py-5">
          <div className="flex items-start gap-3">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-warn-soft text-warn-ink flex-shrink-0">
              <WarningIcon className="h-5 w-5" />
            </div>
            <div>
              <p className="text-[11px] font-mono uppercase tracking-widest text-warn-ink">
                One-time view
              </p>
              <h3 className="font-display text-xl font-bold text-ink mt-0.5">
                Save your API key now
              </h3>
              <p className="text-sm text-ink-muted mt-1">
                You won't be able to see this key again. Save it securely.
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <label className="block text-sm font-medium text-ink-soft mb-1.5">
              Key Label
            </label>
            <input
              type="text"
              value={keyLabel}
              readOnly
              className="w-full rounded-xl border border-paper-300 bg-paper-100 px-4 py-2.5 text-sm text-ink"
            />
          </div>

          {service && (
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1.5">
                Service
              </label>
              <div className="flex items-center gap-2 rounded-xl border border-paper-300 bg-paper-100 px-4 py-2.5">
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-wider ${
                    service === "bigquery"
                      ? "border-note-line bg-note-soft text-note-ink"
                      : "border-paper-300 bg-surface text-ink-soft"
                  }`}
                >
                  {service === "bigquery" ? "BigQuery" : "API"}
                </span>
                <span className="text-xs text-ink-muted">
                  {service === "bigquery"
                    ? "Query your data directly with SQL."
                    : "Call the Telcofy API from your own code."}
                </span>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-ink-soft mb-1.5">
              Service Account
            </label>
            <input
              type="text"
              value={serviceAccountEmail}
              readOnly
              className="w-full rounded-xl border border-paper-300 bg-paper-100 px-4 py-2.5 font-mono text-xs text-ink"
            />
          </div>

          {personalDatasetId && (
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1.5">
                Personal Dataset
              </label>
              <input
                type="text"
                value={personalDatasetId}
                readOnly
                className="w-full rounded-xl border border-paper-300 bg-paper-100 px-4 py-2.5 font-mono text-xs text-ink"
              />
            </div>
          )}

          {realtimeViewId && (
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1.5">
                Realtime Data View
              </label>
              <input
                type="text"
                value={realtimeViewId}
                readOnly
                className="w-full rounded-xl border border-paper-300 bg-paper-100 px-4 py-2.5 font-mono text-xs text-ink"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-ink-soft mb-1.5">
              API Key
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                type="text"
                value={apiKey}
                readOnly
                className="flex-grow rounded-xl border border-paper-300 bg-paper-100 px-4 py-2.5 font-mono text-xs text-ink"
              />
              <button
                onClick={handleCopy}
                className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${
                  copied
                    ? "bg-brand-500 text-on-brand"
                    : "bg-ink text-surface hover:bg-ink-soft hover:-translate-y-0.5"
                }`}
              >
                {copied ? (
                  <>
                    <CheckIcon className="h-4 w-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <CopyIcon className="h-4 w-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>

          <button
            onClick={handleDownload}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-paper-300 bg-surface px-4 py-2.5 text-sm font-semibold text-ink-muted hover:bg-paper-100 hover:text-ink transition-colors"
          >
            <DownloadIcon className="h-4 w-4" />
            Download as file
          </button>

          <label className="flex items-start gap-3 rounded-2xl border border-paper-300 bg-paper-100 p-4 cursor-pointer hover:bg-paper-200/70 transition-colors">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-paper-400 text-brand-500 focus:ring-brand-500"
            />
            <span className="text-sm text-ink-soft">
              I have saved this API key securely.
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-paper-300/70 bg-surface px-6 py-4">
          <button
            onClick={onClose}
            disabled={!confirmed}
            className={`inline-flex items-center rounded-full px-5 py-2 text-sm font-semibold transition-all ${
              confirmed
                ? "bg-brand-500 text-on-brand hover:bg-brand-600 hover:-translate-y-0.5"
                : "bg-paper-200 text-ink-faint cursor-not-allowed"
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApiKeyModal;
